import requester from "api/requester"
import { ISearchFoodsPayload } from "./foodSlice.types"
import {
  ICreateMyFoodPayload,
  IUpdateMyFoodPayload,
} from "model/food/types/foodPayload.types"
import { IMyFood } from "model/food/types/myfood.types"
import { IEdamaFood } from "model/food/types/edama.types"
import { transformObjectKeysToSnakeCase } from "api/converter"
import { IOpenFoodFactFood } from "model/food/types/openFoodFact.types"
import {
  CreateRecentIngredientPayload,
  IRecentIngredientsResponse,
  RecentIngredientType,
} from "model/food/types/recentIngredient.types"
import qs from "qs"

interface IFoodAPI {
  getSuggestedSearchTexts: (
    keyword: string,
    signal: AbortSignal,
  ) => Promise<string[]>

  searchFoods: (payload: ISearchFoodsPayload) => Promise<{
    foods: (IEdamaFood | IMyFood | IOpenFoodFactFood)[]
    next: string
  }>

  getMyFoods: () => Promise<IMyFood[]>
  createFood: (food: ICreateMyFoodPayload) => Promise<IMyFood>
  updateFood: (food: IUpdateMyFoodPayload) => Promise<IMyFood>
  deleteFood: (id: string) => Promise<void>
  getFood: (id: string) => Promise<IMyFood>

  // Recent Ingredients
  getRecentIngredients: (
    params: { page: number; perPage: number },
    signal?: AbortSignal,
  ) => Promise<IRecentIngredientsResponse>
  createRecentIngredient: (
    payload: CreateRecentIngredientPayload,
  ) => Promise<RecentIngredientType>
  deleteRecentIngredient: (id: string) => Promise<void>
}

const foodAPI: IFoodAPI = {
  getSuggestedSearchTexts(keyword, signal) {
    return requester.get(
      `/foods/auto-complete?keyword=${encodeURIComponent(keyword)}`,
      {},
      { signal },
    )
  },

  searchFoods(payload) {
    if (!payload.session) {
      delete payload.session
    }

    return requester.get("/foods/search", payload, {
      paramsSerializer: (params:any) =>
        qs.stringify(params, { arrayFormat: "comma" }),
    })
  },

  getMyFoods() {
    return requester.get("/foods")
  },

  createFood(food) {
    const payload = transformObjectKeysToSnakeCase(food)

    const formData = new FormData()
    Object.keys(payload).forEach((key) => {
      const data = (payload as any)[key]
      if (data === undefined || data === null || data === "") return

      if (key === "ingredients") {
        formData.append(
          key,
          new Blob([JSON.stringify(data)], {
            type: "application/json",
          }),
        )
      } else {
        formData.append(key, data)
      }
    })

    return requester.post("/foods", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },

  updateFood(food) {
    const payload = transformObjectKeysToSnakeCase(food)
    const formData = new FormData()
    Object.keys(payload).forEach((key) => {
      const data = (payload as any)[key]
      if (data === undefined || data === null || data === "") return

      if (key === "ingredients") {
        formData.append(
          key,
          new Blob([JSON.stringify(data)], {
            type: "application/json",
          }),
        )
      } else {
        formData.append(key, data)
      }
    })

    return requester.put(`/foods/${food.id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },

  getFood(id) {
    return requester.get(`/foods/${id}`)
  },

  deleteFood(id) {
    return requester.delete(`/foods/${id}`)
  },

  // Recent Ingredients
  getRecentIngredients({ page, perPage }, signal) {
    return requester.get(
      "/recent-ingredients",
      { page, perPage },
      { signal },
    )
  },

  createRecentIngredient(payload) {
    return requester.post("/recent-ingredients", payload)
  },

  deleteRecentIngredient(id) {
    return requester.delete(`/recent-ingredients/${id}`)
  },
}

export default foodAPI
